import React, {useState} from 'react';
import {Box, Typography, Grid, Tooltip, CircularProgress} from '@mui/material';
import {NAVY, PageHeader, StatCard, BandChip, GuidanceBanner, primaryMarkerSx, secondaryMarkerSx, MARKER_SECONDARY, MARKER_PRIMARY, readinessBandColor} from '../components/widgets.comp';
import { useDashboard } from '../../../context/DashboardContext';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { READINESS_BAND_COLORS } from '../../../utils/constants';
import {isSuppressedItem, isSuppressedBenchmark, kpiNote, whole} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortItems} from '../../DashboardCommon/barSort';

import {fmtValue, benchmarkText} from '../../DashboardCommon/units';
/* A benchmark the datahouse does not send is left out, never shown as a
   number. This school's S_5a carries SCHOOL AVERAGE and GLOBAL AVERAGE but no
   NATIONAL AVERAGE card, and its area items carry only a Global benchmark —
   defaulting to 0 printed "National Average 0" and drew a ▽ marker at the far
   left of every bar, which reads as a real national score of zero. */
const Legend = ({showNational}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
            {Object.keys(READINESS_BAND_COLORS).map((band) => (
                <BandChip key={band} label={band} color={READINESS_BAND_COLORS[band]}/>
            ))}
        </Box>
        {showNational && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${MARKER_PRIMARY}`}}/>
                <Typography sx={{fontSize: '12px', color: '#6A7282'}}>National avg</Typography>
            </Box>
        )}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Global avg</Typography>
        </Box>
    </Box>
);

const AreaBar = ({area, name, score, band, national, global, suppressed}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
        <Box sx={{width: {xs: 130, md: 180}, flexShrink: 0}}>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>{area}</Typography>
            <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY}}>{name}</Typography>
        </Box>
        {suppressed ? <Box sx={{flex: 1}}><SuppressedValue/></Box> : (<>
        <Box sx={{flex: 1, position: 'relative', height: 16, backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: '8px'}}>
            <Tooltip title={fmtValue(score)} arrow placement="top">
                <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${score === null ? 0 : score}%`, backgroundColor: readinessBandColor(band), borderRadius: '8px', cursor: 'pointer'}}/>
            </Tooltip>
            {national != null && (
                <Tooltip title={benchmarkText('National avg', national)} arrow placement="top">
                    <Box sx={primaryMarkerSx(national)}/>
                </Tooltip>
            )}
            {global != null && (
                <Tooltip title={benchmarkText('Global avg', global)} arrow placement="bottom">
                    <Box sx={secondaryMarkerSx(global)}/>
                </Tooltip>
            )}
        </Box>
        <Typography sx={{fontSize: '16px', fontWeight: 800, color: NAVY, width: 34, textAlign: 'right', flexShrink: 0}}>{fmtValue(score)}</Typography>
        <Box sx={{flexShrink: 0}}>{band && <BandChip label={band} color={readinessBandColor(band)}/>}</Box>
        </>)}
    </Box>
);

const READINESS_OVERVIEW_SECTION_CODE = 'S_5a';
const CARD_SECTION = READINESS_OVERVIEW_SECTION_CODE;

const ReadinessOverviewTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    if (loading) {
        return <CircularLoader minHeight="200px" />;
    }

    // Get the S_5 section
    const s5Section = dashboardData?.cards?.sections?.find(s => s.section_code === READINESS_OVERVIEW_SECTION_CODE);
    if (!s5Section) {
        return <NoDataAvailable />;
    }
    const s5Cards = s5Section?.cards || [];

    // Extract dynamic TitleCard if available
    const titleCard = s5Cards.find(c => c.card_type === 'TitleCard');
    const headerTitle = titleCard?.title || "Child Online Safety Readiness Overview";
    const headerSubtitle = titleCard?.footer || "Which child online safety readiness areas need attention across the school?";

    // Extract dynamic KPICards if available
    const schoolAvgCard = s5Cards.find(c => c.kpi_name?.toUpperCase()?.includes('SCHOOL'));
    const nationalAvgCard = s5Cards.find(c => c.kpi_name?.toUpperCase()?.includes('NATIONAL'));
    const globalAvgCard = s5Cards.find(c => c.kpi_name?.toUpperCase()?.includes('GLOBAL'));

    // A KPI can arrive as "—" (no national benchmark for the country) — treat it as missing.
    const truncNum = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Math.trunc(Number(v)));
    const schoolAvg = truncNum(schoolAvgCard?.kpi_value);
    const schoolBand = schoolAvgCard?.kpi_band;
    
    const nationalAvg = truncNum(nationalAvgCard?.kpi_value);
    const nationalBand = nationalAvgCard?.kpi_band;

    const globalAvg = truncNum(globalAvgCard?.kpi_value);
    const globalBand = globalAvgCard?.kpi_band;

    // Extract dynamic BarChartBMCard (competencies) if available
    const barChartCard = s5Cards.find(c => c.card_type === 'BarChartBMCard');
    // A benchmark tick under the n floor (suppressed) is left out like a missing one.
    const benchOf = (item, re, fallback) => {
        const b = item.benchmark_values?.find((x) => re.test(x.name || ''));
        if (!b) return fallback;
        return isSuppressedBenchmark(b) || b.value === undefined || b.value === null ? null : Math.trunc(b.value);
    };
    const dynamicAreas = barChartCard?.items ? barChartCard.items.map((item, index) => {
        const areaNum = item.sublabel ? (item.sublabel.match(/\d+/)?.[0] || (index + 1)) : (index + 1);
        const suppressed = isSuppressedItem(item);
        return {
            area: `Readiness Area ${areaNum}`,
            areaNum: Number(areaNum),
            name: item.label,
            suppressed,
            // TRUNCATE, never round. The band beside a score is derived
            // server-side from the RAW value, so rounding can contradict it:
            // national 49.96 is "Basic", but rounding shows 50 — which reads as
            // "Moderate" (global 50.07). Truncating shows 49 and stays consistent.
            score: suppressed ? null : whole(item.value),
            band: suppressed ? null : (item.band || null),
            national: benchOf(item, /National/, nationalAvg),
            global: benchOf(item, /Global/, globalAvg),
        };
    // Sort by area number — the API doesn't guarantee canonical order (it
    // may return items ranked by score), but each item's own sublabel
    // carries its true fixed position.
    }).sort((a, b) => a.areaNum - b.areaNum) : [];

    // Guidance banner text — over the areas that have a score (an area under the n floor takes no part)
    const scoredAreas = dynamicAreas.filter((a) => a.score !== null);
    const lowestArea = scoredAreas.length > 0
        ? scoredAreas.reduce((min, a) => a.score < min.score ? a : min, scoredAreas[0])
        : null;

    // The comparison names only the benchmarks this school actually has.
    const comparison = (a) => {
        if (a.national != null && a.global != null) return `, below both the national (${a.national}) and global (${a.global}) averages,`;
        if (a.global != null) return `, below the global average (${a.global}),`;
        if (a.national != null) return `, below the national average (${a.national}),`;
        return '';
    };

    const guidanceText = lowestArea
        ? `${lowestArea.area} · ${lowestArea.name} is the lowest area (${lowestArea.score})${comparison(lowestArea)} and is the first focus. If a gap shows school-wide, use school-level Area scores; if it is concentrated, use the grade heatmap before choosing where to act.`
        : (dynamicAreas.length
            ? "Area scores are not shown for this school — fewer than 10 students completed the assessment. Use the participation page to widen coverage before reading the areas."
            : "If a gap shows school-wide, use school-level Area scores; if it is concentrated, use the grade heatmap before choosing where to act.");

    const hasNational = nationalAvg != null || dynamicAreas.some((a) => a.national != null);
    // Group order is the framework order above; the value modes rank the eight areas (suppressed ones last).
    const shownAreas = sortItems(dynamicAreas, sortMode);

    return (
        <Box>
            <PageHeader
                title={headerTitle}
                subtitle={headerSubtitle}
            />

            <Grid container spacing={2.5} sx={{mb: 3}}>
                <Grid item xs={12} md={4}>
                    <StatCard label="School Average" value={schoolAvg == null ? '—' : String(schoolAvg)} band={schoolBand} bandColorOverride={readinessBandColor(schoolBand)} sub={kpiNote(schoolAvgCard)}/>
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard label="National Average" value={nationalAvg == null ? '—' : String(nationalAvg)} band={nationalBand} bandColorOverride={readinessBandColor(nationalBand)} sub={kpiNote(nationalAvgCard)}/>
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard label="Global Average" value={globalAvg == null ? '—' : String(globalAvg)} band={globalBand} bandColorOverride={readinessBandColor(globalBand)} sub={kpiNote(globalAvgCard)}/>
                </Grid>
            </Grid>

            <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5}}>
                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                        {barChartCard?.title || "Readiness Area Scores"}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                        <Legend showNational={hasNational}/>
                        <SortToggle value={sortMode} onChange={setSortMode}/>
                    </Box>
                </Box>
                {shownAreas.map((a) => <AreaBar key={a.area} {...a}/>)}
            </Box>

            <GuidanceBanner text={guidanceText}/>
        </Box>
    );
};

export default ReadinessOverviewTab;
